import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { GraduationCap, Loader2, CheckCircle2, PlayCircle, FileText, Video, Type, Award, ChevronLeft, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ModuleViewer = ({ module }) => {
  if (module.type === 'pdf' && module.file_id) {
    const url = `${API}/training/course-files/${module.file_id}`;
    return (
      <div className="mt-2">
        <embed src={url} type="application/pdf" width="100%" height="500" className="rounded border border-slate-200" />
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#002FA7] hover:underline mt-1 inline-block">Open PDF in new tab</a>
      </div>
    );
  }
  if (module.type === 'video' && module.video_url) {
    // For YouTube/Vimeo, iframe them; else provide the link.
    const yt = module.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
    if (yt) {
      return (
        <iframe title={module.title} className="w-full aspect-video rounded border border-slate-200 mt-2"
          src={`https://www.youtube.com/embed/${yt[1]}`} allowFullScreen />
      );
    }
    return <a href={module.video_url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#002FA7] hover:underline mt-2 inline-block">▶ Watch video</a>;
  }
  if (module.type === 'text' && module.content) {
    return <p className="text-sm text-slate-700 whitespace-pre-wrap mt-2">{module.content}</p>;
  }
  return <p className="text-xs italic text-slate-400 mt-2">(No content)</p>;
};

const CourseRunner = ({ course, onBack, onCompleted }) => {
  const [enrolling, setEnrolling] = useState(false);
  const [enrollment, setEnrollment] = useState(course.enrollment || null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const enroll = async () => {
    setEnrolling(true);
    try {
      const r = await axios.post(`${API}/training/courses/${course.id}/enroll`, {}, { withCredentials: true });
      setEnrollment(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Enroll failed');
    } finally { setEnrolling(false); }
  };

  const submit = async () => {
    if ((course.quiz || []).length === 0) {
      toast.error('This course has no quiz');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/training/courses/${course.id}/submit-quiz`,
        { answers }, { withCredentials: true });
      setResult(r.data);
      if (r.data.passed) {
        toast.success(`Passed with ${r.data.score_pct}%!`);
        onCompleted?.();
      } else {
        toast.error(`Score ${r.data.score_pct}% · Needed ${r.data.passing_score}%`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div>
      <button onClick={onBack} data-testid="back-to-courses"
        className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-4">
        <ChevronLeft className="w-4 h-4" /> Back to my courses
      </button>
      <div className="bg-white border border-slate-200 rounded-md p-6" data-testid={`course-runner-${course.id}`}>
        <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{course.title}</h2>
        {course.description && <p className="text-sm text-slate-600 mt-1">{course.description}</p>}

        {!enrollment && (
          <div className="mt-4">
            <button onClick={enroll} disabled={enrolling} data-testid="enroll-btn"
              className="inline-flex items-center gap-2 bg-[#002FA7] text-white text-sm font-semibold rounded-md px-4 py-2 hover:bg-[#00247D] disabled:opacity-50">
              {enrolling && <Loader2 className="w-4 h-4 animate-spin" />}
              Start course
            </button>
          </div>
        )}

        {enrollment && (
          <>
            <div className="mt-4 space-y-4">
              {(course.modules || []).map((m, i) => (
                <div key={m.id} className="border border-slate-200 rounded-md p-4" data-testid={`view-module-${m.id}`}>
                  <div className="flex items-center gap-2">
                    {m.type === 'pdf' && <FileText className="w-4 h-4 text-slate-500" />}
                    {m.type === 'video' && <Video className="w-4 h-4 text-slate-500" />}
                    {m.type === 'text' && <Type className="w-4 h-4 text-slate-500" />}
                    <h4 className="text-sm font-semibold text-slate-900">{i + 1}. {m.title}</h4>
                  </div>
                  <ModuleViewer module={m} />
                </div>
              ))}
            </div>

            {(course.quiz || []).length > 0 && (
              <div className="mt-6 border-t border-slate-200 pt-6">
                <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Quiz</h3>
                <p className="text-xs text-slate-500 mb-4">Score {course.passing_score || 70}% or higher to pass.</p>
                <div className="space-y-4">
                  {course.quiz.map((q, qi) => (
                    <div key={q.id} className="border border-slate-200 rounded-md p-3" data-testid={`quiz-q-${q.id}`}>
                      <p className="text-sm font-medium text-slate-900">Q{qi + 1}. {q.question}</p>
                      <div className="mt-2 space-y-1">
                        {q.choices.map((c, ci) => (
                          <label key={ci} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 hover:text-slate-900">
                            <input type="radio" name={q.id} checked={answers[q.id] === ci}
                              onChange={() => setAnswers({ ...answers, [q.id]: ci })}
                              className="accent-[#002FA7]"
                              data-testid={`quiz-choice-${q.id}-${ci}`} />
                            {c}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <button onClick={submit} disabled={submitting} data-testid="submit-quiz"
                    className="inline-flex items-center gap-2 bg-[#002FA7] text-white text-sm font-semibold rounded-md px-4 py-2 hover:bg-[#00247D] disabled:opacity-50">
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    Submit quiz
                  </button>
                  {result && (
                    <div className={`text-sm font-semibold ${result.passed ? 'text-emerald-700' : 'text-red-700'}`} data-testid="quiz-result">
                      {result.passed ? `Passed with ${result.score_pct}%` : `Score ${result.score_pct}% · Try again`}
                    </div>
                  )}
                </div>
                {result?.passed && result.certificate_id && (
                  <a href={`${API}/training/certificates/${result.certificate_id}/pdf`} target="_blank" rel="noopener noreferrer"
                    data-testid="download-cert"
                    className="mt-3 inline-flex items-center gap-2 text-sm text-[#002FA7] hover:text-[#00247D] font-semibold">
                    <Download className="w-4 h-4" /> Download your certificate
                  </a>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const MyTraining = () => {
  const [courses, setCourses] = useState([]);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, ce] = await Promise.all([
        axios.get(`${API}/training/my-courses`, { withCredentials: true }),
        axios.get(`${API}/training/certificates/mine`, { withCredentials: true }),
      ]);
      setCourses(c.data); setCerts(ce.data);
    } catch { toast.error('Could not load training'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8" data-testid="my-training-page">
        {current ? (
          <CourseRunner course={current} onBack={() => { setCurrent(null); fetchAll(); }} onCompleted={fetchAll} />
        ) : (
          <>
            <div className="mb-6">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-6 h-6 text-[#002FA7]" />
                <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  My Training
                </h1>
              </div>
              <p className="text-sm text-slate-600 mt-1">Courses assigned to you and certificates earned.</p>
            </div>

            {certs.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
                  <Award className="w-4 h-4" /> Your certificates
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {certs.map((c) => (
                    <div key={c.id} className="bg-white border border-emerald-200 rounded-md p-4" data-testid={`cert-card-${c.id}`}>
                      <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Certificate</p>
                      <p className="text-sm font-bold text-slate-900 mt-1">{c.course_title}</p>
                      <p className="text-xs text-slate-500 mt-1">#{c.certificate_no}</p>
                      {c.ceu_credits ? (
                        <p className="text-xs text-[#002FA7] font-semibold mt-1" data-testid={`cert-ceu-${c.id}`}>
                          {c.ceu_credits} CEU credit{Number(c.ceu_credits) !== 1 ? 's' : ''}
                        </p>
                      ) : null}
                      <a href={`${API}/training/certificates/${c.id}/pdf`} target="_blank" rel="noopener noreferrer"
                        data-testid={`download-cert-${c.id}`}
                        className="mt-2 inline-flex items-center gap-1 text-xs text-[#002FA7] hover:text-[#00247D] font-semibold">
                        <Download className="w-3.5 h-3.5" /> Download PDF
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-2">Assigned courses</h2>
            {loading ? (
              <div className="p-10 text-center text-slate-500 bg-white border border-slate-200 rounded-md">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
              </div>
            ) : courses.length === 0 ? (
              <div className="p-10 text-center text-slate-500 bg-white border border-slate-200 rounded-md" data-testid="my-training-empty">
                No courses assigned to you yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {courses.map((c) => {
                  const status = c.enrollment?.status;
                  return (
                    <button key={c.id} onClick={() => setCurrent(c)}
                      data-testid={`start-course-${c.id}`}
                      className="text-left bg-white border border-slate-200 rounded-md p-4 hover:border-[#002FA7] hover:shadow-sm transition-all">
                      <div className="flex items-center gap-2">
                        <PlayCircle className="w-4 h-4 text-[#002FA7]" />
                        <h3 className="text-sm font-semibold text-slate-900 line-clamp-1">{c.title}</h3>
                      </div>
                      {c.description && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{c.description}</p>}
                      <div className="mt-3 flex items-center justify-between text-xs">
                        <span className="text-slate-500">{(c.modules || []).length} modules · {(c.quiz || []).length} questions</span>
                        {status === 'passed' ? (
                          <span className="text-emerald-700 flex items-center gap-1 font-semibold">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Passed
                          </span>
                        ) : status === 'in_progress' ? (
                          <span className="text-amber-700 font-semibold">In progress</span>
                        ) : (
                          <span className="text-[#002FA7] font-semibold">Start</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MyTraining;
